import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Users, ChefHat, ArrowLeft, Fish, CheckCircle, BookOpen } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './RecipeDetailPage.css';

const DIFFICULTY_COLOR = { EASY: '#10B981', MEDIUM: '#F59E0B', HARD: '#EF4444' };

const RecipeDetailPage = () => {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchRecipe();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchRecipe = async () => {
    try {
      const res = await axiosInstance.get(`/recipes/${id}`);
      if (res.data.success) {
        setRecipe(res.data.data);
      } else {
        setNotFound(true);
      }
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <>
      <Navbar />
      <div className="recipe-detail-loading">
        <div className="spinner" />
        <p>Loading recipe...</p>
      </div>
      <Footer />
    </>
  );

  if (notFound) return (
    <>
      <Navbar />
      <div className="recipe-detail-notfound">
        <Fish size={64} color="#29ABE2" strokeWidth={1} />
        <h2>Recipe not found</h2>
        <p>This recipe may have been removed or is not yet published.</p>
        <Link to="/recipes" className="recipe-back-btn"><ArrowLeft size={16} /> Back to Recipes</Link>
      </div>
      <Footer />
    </>
  );

  const ingredients = recipe.ingredients?.split('|').filter(Boolean) || [];
  const steps = recipe.steps?.split('|').filter(Boolean) || [];
  const tags = recipe.tags?.split(',').filter(Boolean) || [];
  const totalTime = (recipe.prepTimeMinutes || 0) + (recipe.cookTimeMinutes || 0);

  return (
    <>
      <Navbar />
      <div className="recipe-detail-page">
        {/* Hero */}
        <div className="recipe-detail-hero" style={recipe.imageUrl ? { backgroundImage: `linear-gradient(to bottom, rgba(13,33,55,0.3), rgba(13,33,55,0.85)), url(${recipe.imageUrl})` } : {}}>
          <div className="container">
            <Link to="/recipes" className="recipe-back-link"><ArrowLeft size={16} /> All Recipes</Link>
            <div className="recipe-detail-hero-content">
              {recipe.category && <span className="recipe-detail-cat">{recipe.category}</span>}
              <h1 className="recipe-detail-title">{recipe.title}</h1>
              {recipe.description && <p className="recipe-detail-desc">{recipe.description}</p>}
              <div className="recipe-detail-meta">
                {recipe.difficulty && (
                  <span className="recipe-meta-badge" style={{ background: DIFFICULTY_COLOR[recipe.difficulty] }}>
                    {recipe.difficulty}
                  </span>
                )}
                {recipe.prepTimeMinutes && (
                  <span className="recipe-meta-item"><Clock size={14} /> Prep: {recipe.prepTimeMinutes} min</span>
                )}
                {recipe.cookTimeMinutes && (
                  <span className="recipe-meta-item"><Clock size={14} /> Cook: {recipe.cookTimeMinutes} min</span>
                )}
                {totalTime > 0 && (
                  <span className="recipe-meta-item recipe-meta-total"><Clock size={14} /> Total: {totalTime} min</span>
                )}
                {recipe.servings && (
                  <span className="recipe-meta-item"><Users size={14} /> {recipe.servings} servings</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="recipe-detail-body">
            {/* Ingredients */}
            <div className="recipe-ingredients-card">
              <h2 className="recipe-section-title"><ChefHat size={20} /> Ingredients</h2>
              <p className="recipe-serves-note">For {recipe.servings || 2} person{recipe.servings !== 1 ? 's' : ''}</p>
              <ul className="recipe-ingredients-list">
                {ingredients.map((ing, i) => (
                  <li key={i} className="recipe-ingredient-item">
                    <span className="recipe-ingredient-dot" />
                    {ing.trim()}
                  </li>
                ))}
              </ul>

              {tags.length > 0 && (
                <div className="recipe-tags">
                  {tags.map((tag, i) => (
                    <span key={i} className="recipe-tag">{tag.trim()}</span>
                  ))}
                </div>
              )}

              {/* CTA */}
              <div className="recipe-shop-cta">
                <Fish size={20} color="#29ABE2" />
                <div>
                  <p className="recipe-shop-cta-title">Get Fresh Ingredients</p>
                  <p className="recipe-shop-cta-desc">Order fresh seafood from Aurora delivered to your door.</p>
                </div>
                <Link to="/shop" className="recipe-shop-btn">Shop Now</Link>
              </div>
            </div>

            {/* Steps */}
            <div className="recipe-steps-section">
              <h2 className="recipe-section-title"><BookOpen size={20} /> Instructions</h2>
              <div className="recipe-steps-list">
                {steps.map((step, i) => (
                  <div
                    key={i}
                    className={`recipe-step ${activeStep === i ? 'recipe-step-active' : ''} ${activeStep > i ? 'recipe-step-done' : ''}`}
                    onClick={() => setActiveStep(i)}
                  >
                    <div className="recipe-step-number">
                      {activeStep > i ? <CheckCircle size={20} color="#10B981" /> : <span>{i + 1}</span>}
                    </div>
                    <div className="recipe-step-content">
                      <p className="recipe-step-label">Step {i + 1}</p>
                      <p className="recipe-step-text">{step.trim()}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Step progress */}
              <div className="recipe-step-progress">
                <div className="recipe-step-progress-bar">
                  <div className="recipe-step-progress-fill" style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }} />
                </div>
                <p className="recipe-step-progress-text">Step {activeStep + 1} of {steps.length}</p>
                <div className="recipe-step-nav">
                  <button disabled={activeStep === 0} onClick={() => setActiveStep(p => p - 1)} className="recipe-step-nav-btn">← Previous</button>
                  <button disabled={activeStep === steps.length - 1} onClick={() => setActiveStep(p => p + 1)} className="recipe-step-nav-btn recipe-step-nav-next">Next →</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Request section */}
        <div className="recipe-request-section">
          <div className="container">
            <div className="recipe-request-inner">
              <BookOpen size={28} color="#29ABE2" strokeWidth={1.5} />
              <div>
                <h3>Want a Specific Recipe?</h3>
                <p>Can't find what you're looking for? Request a recipe from our chefs!</p>
              </div>
              <a href="https://app.auroragh.com/dashboard/recipes" className="recipe-request-btn">Request a Recipe</a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RecipeDetailPage;